// Eskom PARCC — pure dashboard-export model (WS1, Lindiwe R4 feedback #1).
//
// The dashboard is a read-only cockpit; Lindiwe asked to download it "to enhance
// our reports". Rather than screenshot the DOM, we shape the SAME figures the
// dashboard already computes (computeDashboard + derive.ts helpers) into a plain
// data model, and hand it to native PPTX / PDF / XLSX builders — so a downloaded
// dashboard equals what is on screen (grant-scoped + facet-filtered) and carries
// crisp vector charts. This module owns NO maths: every number is passed in from
// the page's `view`. Deterministic (dates passed in, never read from the clock)
// and lib-free, so it is unit-testable as data.
import type { DashboardKpis } from '../../../domain/dashboard';
import type { ReportKpiTile } from '../../reports/buildReportModel';
import type { BreachBucket, ThemeBucket, DivisionStat } from '../derive';
import type { FindingSummary } from '../../../domain/findings';
import { FACET_DEFS, type FacetState } from '../../../domain/filters';
import { formatRand } from '../../../lib/money';
import { DISTRIBUTION_LEVELS, levelColour } from '../palette';

export type { ReportKpiTile };

/** Scoping + presentation meta — all passed in, none derived from the clock. */
export interface DashboardExportMeta {
  /** Human date, e.g. "13 July 2026" (formatted by the caller). */
  dateLong: string;
  /** ISO timestamp the export was generated (determinism + audit). */
  generatedAtIso: string;
  /** Who generated it (grant email). */
  generatedBy: string;
  /** Role-scope description, e.g. "All divisions (administrator view)". */
  scopeNote: string;
  /** Active facet-filter descriptions; a friendly line when none are set. */
  filters: string[];
}

/** One compliance-distribution slice for the export donut. */
export interface DashboardDonutSlice {
  level: string;
  count: number;
  /** Percentage of the in-view population (0–100, rounded). */
  pct: number;
  /** Brand colour (dashboard palette), with the leading '#'. */
  colour: string;
}

/** The reconciliation identity Lindiwe asked to see (Jul 8 2026). */
export interface DashboardReconciliation {
  inView: number;
  reviewed: number;
  pending: number;
  compliant: number;
  /** UNRESOLVED exceptions only — self-declarations are counted separately (R7). */
  nonCompliant: number;
  /** Exceptions resolved by self-declaration (R7, Lindiwe 27 Jul 2026). */
  selfDeclared: number;
  /** Not Applicable + flagged-for-removal, under one umbrella (Lindiwe, 15 Sep 2026). */
  anomalies: number;
}

/** The complete, pure dashboard export model. */
export interface DashboardExportModel {
  meta: DashboardExportMeta;
  kpis: DashboardKpis;
  /** The eleven headline tiles, formatted as label/value/tone (report-tile parity). */
  tiles: ReportKpiTile[];
  reconciliation: DashboardReconciliation;
  riskScore: number;
  riskBand: string;
  donut: DashboardDonutSlice[];
  breaches: BreachBucket[];
  themes: ThemeBucket[];
  divisions: DivisionStat[];
  /** Distinct genuine findings split by disposition (outstanding is the headline). */
  findings: FindingSummary;
}

/** Band label for the 0–100 risk score (shared with the on-screen gauge). */
export function riskBand(score: number): string {
  if (score >= 67) return 'High';
  if (score >= 34) return 'Elevated';
  return 'Low';
}

/** Human descriptions of the active facet filters ('' value ⇒ inactive). */
export function describeFilters(facets: FacetState): string[] {
  const active = FACET_DEFS.filter((d) => (facets[d.key] ?? '') !== '').map((d) => `${d.label}: ${facets[d.key]}`);
  return active.length > 0 ? active : ['No filters applied — full population in view'];
}

/**
 * The headline tiles, in the on-screen order, formatted as report tiles.
 *
 * R7 (27 Jul 2026): mirrors the Dashboard's self-declaration split — the single
 * "Value in Possible IE" tile became unresolved + self-declared, and Self-Declared
 * joined the reviewed-outcome row. R9 (Aug 2026): the "Findings" tile now reads
 * OUTSTANDING of identified (the closure model reworked from the 30 Jul hide) — a
 * declared or resolved finding no longer counts as outstanding. The exported deck
 * and the screen must never show a different split of the same population.
 */
function buildTiles(kpis: DashboardKpis, findings: FindingSummary): ReportKpiTile[] {
  const reviewedPct = kpis.totalTransactions > 0 ? Math.round((kpis.reviewed / kpis.totalTransactions) * 100) : 0;
  const n = (x: number): string => x.toLocaleString('en-ZA');
  return [
    { label: 'Population', value: n(kpis.totalTransactions), tone: 'neutral' },
    { label: 'Reviewed', value: `${n(kpis.reviewed)} (${reviewedPct}%)`, tone: 'good' },
    // Outstanding of identified — the addressed work (self-declared + resolved) is the
    // difference, so the snapshot no longer reads as "nothing resolved".
    { label: 'Findings', value: `${n(findings.outstanding)} of ${n(findings.identified)}`, tone: 'warn' },
    {
      label: 'Possible IE · unresolved',
      value: formatRand(kpis.possibleIeValueUnresolved) || 'R 0',
      tone: 'bad',
    },
    {
      label: 'Possible IE · self-declared',
      value: formatRand(kpis.possibleIeValueSelfDeclared) || 'R 0',
      tone: 'warn',
    },
    { label: 'Compliant', value: n(kpis.compliant), tone: 'good' },
    { label: 'Non-Compliant', value: n(kpis.nonCompliant), tone: 'bad' },
    { label: 'Self-Declared', value: n(kpis.selfDeclared), tone: 'warn' },
    { label: 'Anomalies', value: n(kpis.anomalies), tone: 'neutral' },
  ];
}

/** The compliance-distribution donut slices (non-empty levels only, with %). */
function buildDonut(kpis: DashboardKpis): DashboardDonutSlice[] {
  const total = DISTRIBUTION_LEVELS.reduce((s, level) => s + (kpis.byLevel[level]?.count ?? 0), 0);
  return DISTRIBUTION_LEVELS.filter((level) => (kpis.byLevel[level]?.count ?? 0) > 0).map((level) => {
    const count = kpis.byLevel[level]?.count ?? 0;
    return { level, count, pct: total > 0 ? Math.round((count / total) * 100) : 0, colour: levelColour(level) };
  });
}

/**
 * Assemble the pure dashboard export model from the page's already-computed view.
 * The caller passes the same `kpis` / `breaches` / `themes` / `divisions` the
 * dashboard renders, so the export cannot diverge from the screen.
 */
export function buildDashboardExportModel(args: {
  kpis: DashboardKpis;
  findings: FindingSummary;
  breaches: BreachBucket[];
  themes: ThemeBucket[];
  divisions: DivisionStat[];
  facets: FacetState;
  meta: { dateLong: string; generatedAtIso: string; generatedBy: string; scopeNote: string };
}): DashboardExportModel {
  const { kpis, findings, breaches, themes, divisions, facets, meta } = args;
  return {
    meta: { ...meta, filters: describeFilters(facets) },
    kpis,
    tiles: buildTiles(kpis, findings),
    reconciliation: {
      inView: kpis.totalTransactions,
      reviewed: kpis.reviewed,
      pending: kpis.pending,
      compliant: kpis.compliant,
      selfDeclared: kpis.selfDeclared,
      nonCompliant: kpis.nonCompliant,
      anomalies: kpis.anomalies,
    },
    riskScore: kpis.riskScore,
    riskBand: riskBand(kpis.riskScore),
    donut: buildDonut(kpis),
    breaches,
    themes,
    divisions,
    findings,
  };
}
